const UTAG_POLL_INTERVAL = 200;
const UTAG_MAX_ATTEMPTS = 50; // 10 seconds max

type QueuedCall = { type: 'view' | 'link'; tags: any };

const queue: QueuedCall[] = [];
let polling = false;

/** Numeric club code → Tealium club abbreviation (leading zeros optional). */
const CLUB_CODE_TO_ABBR: Record<number, string> = {
  1: 'al',
  4: 'ca',
  18: 'hi',
  36: 'nne',
  65: 'acmo',
  188: 'nhc',
  215: 'ec',
  240: 'ane',
  252: 'tx',
  258: 'tw',
  601: 'nm',
};

function withMappedClub(tags: any) {
  if (!tags || tags.clubcode == null || tags.clubcode === '') {
    return { ...tags };
  }

  const numericCode = parseInt(String(tags.clubcode), 10);
  if (Number.isNaN(numericCode)) {
    return { ...tags };
  }

  const abbr = CLUB_CODE_TO_ABBR[numericCode];
  if (!abbr) {
    return { ...tags };
  }

  return { ...tags, club: abbr };
}

function isUtagReady(): boolean {
  const utag = (global as any).utag;
  if (typeof utag === 'undefined' || typeof utag.view !== 'function' || typeof utag.link !== 'function') {
    return false;
  }
  if (!utag.sender || typeof utag.sender !== 'object') return false;
  const senderKeys = Object.keys(utag.sender);
  if (senderKeys.length === 0) return false;
  return senderKeys.some((key) => utag.sender[key]?.initialized !== false);
}

function flushQueue() {
  while (queue.length > 0) {
    const call = queue.shift()!;
    if (call.type === 'view') {
      (global as any).utag.view(call.tags);
    } else {
      (global as any).utag.link(call.tags);
    }
  }
}

function startPolling() {
  if (polling) return;
  polling = true;
  let attempts = 0;

  const interval = setInterval(() => {
    attempts++;
    if (isUtagReady()) {
      clearInterval(interval);
      polling = false;
      flushQueue();
    } else if (attempts >= UTAG_MAX_ATTEMPTS) {
      clearInterval(interval);
      polling = false;
      queue.length = 0;
    }
  }, UTAG_POLL_INTERVAL);
}

export class UtagHelper {
  static loadUtagJs() {
    if ((global as any).utagLoadAttempt) return;
    (global as any).utagLoadAttempt = true;
    const utagJsScript = document.createElement('script');
    utagJsScript.src = '//tms.ace.aaa.com/ace/prod/utag.js';
    utagJsScript.type = 'text/javascript';
    utagJsScript.async = true;
    utagJsScript.onload = () => {
      if (isUtagReady()) {
        flushQueue();
      } else {
        startPolling();
      }
    };
    document.body.appendChild(utagJsScript);
  }

  static tag(tags: any) {
    const mappedTags = withMappedClub(tags);
    (global as any).aceMediaTagValues = mappedTags;
    if (isUtagReady()) {
      (global as any).utag.view(mappedTags);
    } else {
      queue.push({ type: 'view', tags: mappedTags });
      if (typeof (global as any).utag === 'undefined') {
        this.loadUtagJs();
      }
      startPolling();
    }
  }

  static link(tags: any) {
    const mappedTags = withMappedClub(tags);
    (global as any).aceMediaTagValues = mappedTags;
    if (isUtagReady()) {
      (global as any).utag.link(mappedTags);
    } else {
      queue.push({ type: 'link', tags: mappedTags });
      if (typeof (global as any).utag === 'undefined') {
        this.loadUtagJs();
      }
      startPolling();
    }
  }

  static tagError(tags: any, page: string, error: string) {
    if (!tags || Object.keys(tags).length === 0) return {};

    let _page = tags.page || '';

    let tagPages: string[] = _page.split(':');
    tagPages.pop();

    return {
      ...tags,
      page: [...tagPages, page].join(':'),
      error: [...tagPages, error].join(':'),
    };
  }

  static tagPage(tags: any, page: string) {
    if (!tags || Object.keys(tags).length === 0) return {};

    let _page = tags.page || '';

    let tagPages: string[] = _page.split(':');
    tagPages.pop();

    return {
      ...tags,
      page: [...tagPages, page].join(':'),
    };
  }
}
